import React from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, PolarGrid,
  PolarAngleAxis as PolarAngleAxisBase, PolarRadiusAxis as PolarRadiusAxisBase,
  Radar,
} from 'recharts';
import { HolisticStudent } from '../../../types';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarAngleAxis = PolarAngleAxisBase as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarRadiusAxis = PolarRadiusAxisBase as any;

const STAT_CARDS = [
  { key: 'Term 1 Competency', bg: 'from-indigo-50 to-violet-50',   border: 'border-indigo-100', dot: 'bg-gradient-to-br from-indigo-400 to-violet-500',   val: '' },
  { key: 'Term 2 Competency', bg: 'from-violet-50 to-purple-50',   border: 'border-violet-100', dot: 'bg-gradient-to-br from-violet-400 to-purple-500',   val: '' },
  { key: 'Overall Average',   bg: 'from-amber-50 to-orange-50',    border: 'border-amber-100',  dot: 'bg-gradient-to-br from-amber-400 to-orange-400',    val: '' },
  { key: 'Attendance',        bg: 'from-purple-50 to-indigo-50',   border: 'border-purple-100', dot: 'bg-gradient-to-br from-purple-400 to-indigo-400',   val: '' },
];

const StatCard: React.FC<{ label: string; value: string; bg: string; border: string; dot: string; desc?: string }> = ({ label, value, bg, border, dot, desc }) => (
  <div className={`rounded-xl border ${border} bg-gradient-to-br ${bg} p-4 shadow-sm flex flex-col justify-between`}>
    <div>
      <div className={`w-8 h-8 rounded-lg ${dot} mb-3 shadow-sm`} />
      <p className="text-xl font-bold text-gray-800">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
    {desc && <p className="text-[10px] text-indigo-755 bg-indigo-50/60 px-2 py-0.5 rounded border border-indigo-100/50 w-fit font-bold mt-2 shadow-xs">{desc}</p>}
  </div>
);

const RingCard: React.FC<{ label: string; value: number; stroke: string; bg: string; border: string }> = ({ label, value, stroke, bg, border }) => {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, value) / 100) * circ;
  const lvl = value >= 85 ? 'Excellent' : value >= 70 ? 'Good' : value >= 55 ? 'Average' : 'Below Avg';
  return (
    <div className={`rounded-xl border ${border} bg-gradient-to-br ${bg} p-4 shadow-sm flex items-center gap-4`}>
      <svg width="84" height="84" viewBox="0 0 84 84" className="flex-shrink-0">
        <circle cx="42" cy="42" r={r} fill="none" stroke="#E5E7EB" strokeWidth="8" />
        <circle
          cx="42" cy="42" r={r}
          fill="none" stroke={stroke} strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          transform="rotate(-90 42 42)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="42" y="47" textAnchor="middle" fontSize="14" fontWeight="700" fill={stroke}>{value}</text>
      </svg>
      <div>
        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
        <p className="text-sm font-bold text-gray-800">{lvl}</p>
        <p className="text-xs text-gray-400">{value} / 100</p>
      </div>
    </div>
  );
};

const OverviewTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();

  const totalDaysPresent = Object.values(student.attendance.daysAttendedPerMonth).reduce((a, b) => a + b, 0);
  const totalSchoolDays = student.attendance.totalSchoolDaysPerMonth && Object.values(student.attendance.totalSchoolDaysPerMonth).some(v => v > 0)
    ? Object.values(student.attendance.totalSchoolDaysPerMonth).reduce((a, b) => a + b, 0)
    : Object.keys(student.attendance.daysAttendedPerMonth).length * 22;

  const attendanceData = Object.entries(student.attendance.daysAttendedPerMonth).map(([month, days]) => {
    const total = student.attendance.totalSchoolDaysPerMonth?.[month] !== undefined
      ? student.attendance.totalSchoolDaysPerMonth[month]
      : 22;
    return {
      month,
      present: days,
      total,
    };
  });

  const overviewRadar = [
    { subject: 'Academics',  value: Math.round((student.academic.term1_competency + student.academic.term2_competency) / 2) },
    { subject: 'Behavior',   value: student.behaviorScore },
    { subject: 'Peer',       value: student.peerScore },
    { subject: 'Attendance', value: student.attendancePercentage },
    { subject: 'Physical',   value: student.physical.bmiCategory === 'Normal' ? 85 : student.physical.bmiCategory === 'Underweight' ? 50 : 60 },
  ];

  const avgCompetency = Math.round((student.academic.term1_competency + student.academic.term2_competency) / 2);

  const statValues = [
    `${student.academic.term1_competency}%`,
    `${student.academic.term2_competency}%`,
    `${avgCompetency}%`,
    `${student.attendancePercentage}%`,
  ];

  const statDescs = [
    'Term 1 Evaluated',
    'Term 2 Evaluated',
    'Overall Academic',
    totalSchoolDays > 0 ? `Present: ${totalDaysPresent} / ${totalSchoolDays} days` : 'Yearly Attendance',
  ];

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STAT_CARDS.map((c, i) => (
          <StatCard
            key={c.key}
            label={c.key}
            value={statValues[i]}
            bg={c.bg}
            border={c.border}
            dot={c.dot}
            desc={statDescs[i]}
          />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Radar */}
        <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-violet-50/30 p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-4">Holistic Development Overview</p>
          <ResponsiveContainer width="100%" height={270}>
            <RadarChart data={overviewRadar}>
              <defs>
                <radialGradient id="radarFill" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0.04} />
                </radialGradient>
              </defs>
              <PolarGrid stroke="#E0E7FF" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#6B7280' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
              <Radar dataKey="value" stroke="#6366F1" fill="url(#radarFill)" strokeWidth={2} dot={{ fill: '#6366F1', r: 3, stroke: '#fff', strokeWidth: 1.5 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E0E7FF', fontSize: 12, background: 'rgba(255,255,255,0.95)' }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart */}
        <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/50 to-purple-50/30 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-sm font-semibold text-gray-700">Monthly Attendance (Days)</p>
            <div className="flex items-center gap-3 text-[10px] font-bold">
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded bg-gray-300 inline-block" />
                <span className="text-gray-600">Total Open</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded bg-indigo-500 inline-block" />
                <span className="text-gray-600">Present</span>
              </div>
            </div>
          </div>

          {/* Comparison Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-purple-100/50 shadow-sm">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">School Open</span>
              <p className="text-base font-extrabold text-gray-800 mt-0.5">{totalSchoolDays} Days</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-purple-100/50 shadow-sm">
              <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block">Present</span>
              <p className="text-base font-extrabold text-indigo-600 mt-0.5">{totalDaysPresent} Days</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-purple-100/50 shadow-sm">
              <span className="text-[10px] font-bold text-rose-500 uppercase tracking-wider block">Absent</span>
              <p className="text-base font-extrabold text-rose-500 mt-0.5">{Math.max(0, totalSchoolDays - totalDaysPresent)} Days</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-purple-100/50 shadow-sm">
              <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider block">Rate</span>
              <p className="text-base font-extrabold text-emerald-600 mt-0.5">
                {totalSchoolDays > 0 ? ((totalDaysPresent / totalSchoolDays) * 100).toFixed(1) : student.attendancePercentage}%
              </p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={270}>
            <BarChart data={attendanceData} barGap={3} barCategoryGap="20%">
              <defs>
                <linearGradient id="presentFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id="totalFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E5E7EB" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#D1D5DB" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDE9FE" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #EDE9FE', fontSize: 12, background: 'rgba(255,255,255,0.95)' }} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
              <Bar dataKey="total" name="Total Open Days" fill="url(#totalFill)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="present" name="Days Present" fill="url(#presentFill)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Progress Rings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RingCard label="Term 1 Competency" value={student.academic.term1_competency} stroke="#6366F1" bg="from-indigo-50 to-violet-50/60" border="border-indigo-100" />
        <RingCard label="Term 2 Competency" value={student.academic.term2_competency} stroke="#7C3AED" bg="from-violet-50 to-purple-50/60" border="border-violet-100" />
        <RingCard label="Behavior Score"    value={student.behaviorScore}              stroke="#F59E0B" bg="from-amber-50 to-orange-50/60"  border="border-amber-100" />
      </div>
    </div>
  );
};

export default OverviewTab;
